export const ALLOWED_OPTION_COUNTS = [2, 4] as const;
export type AllowedOptionCount = (typeof ALLOWED_OPTION_COUNTS)[number];

export function isAllowedOptionCount(n: number): n is AllowedOptionCount {
  return (ALLOWED_OPTION_COUNTS as readonly number[]).includes(n);
}

export function isValidImportQuestion(q: unknown): boolean {
  if (!q || typeof q !== "object") return false;
  const row = q as Record<string, unknown>;
  if (typeof row.text !== "string" || !row.text.trim()) return false;
  if (typeof row.explanation !== "string" || !row.explanation.trim()) return false;
  if (typeof row.category !== "string" || !row.category.trim()) return false;
  if (!Array.isArray(row.options)) return false;

  const options = row.options.map((o) => String(o).trim()).filter(Boolean);
  if (!isAllowedOptionCount(options.length)) return false;

  const idx = row.correctOptionIndex;
  if (typeof idx !== "number" || !Number.isInteger(idx) || idx < 0 || idx >= options.length) {
    return false;
  }

  const textFr = row.text_fr ?? row.textFr;
  const optionsFr = row.options_fr ?? row.optionsFr;
  const explanationFr = row.explanation_fr ?? row.explanationFr;
  const hasFr =
    typeof textFr === "string" && textFr.trim() &&
    typeof explanationFr === "string" && explanationFr.trim();

  if (!hasFr) return true;
  if (!Array.isArray(optionsFr)) return false;
  const fr = optionsFr.map((o) => String(o).trim());
  return fr.length === options.length && fr.every((o) => o.length > 0);
}

export function questionFormFromApi(q: {
  options: string[];
  optionsFr?: string[] | null;
  correctOptionIndex: number;
  text: string;
  explanation: string;
  category: string;
  difficulty?: string | null;
  imageUrl?: string | null;
  explanationImageUrl?: string | null;
  textFr?: string | null;
  explanationFr?: string | null;
  referenceUrl?: string | null;
}): {
  text: string;
  options: [string, string, string, string];
  optionsFr: [string, string, string, string];
  optionCount: AllowedOptionCount;
  correctOptionIndex: number;
  explanation: string;
  category: string;
  difficulty: string;
  imageUrl: string | null;
  explanationImageUrl: string | null;
  textFr: string;
  explanationFr: string;
  referenceUrl: string;
} {
  const optionCount: AllowedOptionCount = q.options.length === 2 ? 2 : 4;
  const pad4 = (arr: string[]) => [...arr, "", "", ""].slice(0, 4) as [string, string, string, string];
  return {
    text: q.text,
    options: pad4(q.options),
    optionsFr: pad4(q.optionsFr ?? []),
    optionCount,
    correctOptionIndex: Math.min(q.correctOptionIndex, optionCount - 1),
    explanation: q.explanation,
    category: q.category,
    difficulty: q.difficulty ?? "medium",
    imageUrl: q.imageUrl ?? null,
    explanationImageUrl: q.explanationImageUrl ?? null,
    textFr: q.textFr ?? "",
    explanationFr: q.explanationFr ?? "",
    referenceUrl: q.referenceUrl ?? "",
  };
}

export function serializeQuestionForm(form: {
  text: string;
  options: string[];
  optionsFr: string[];
  optionCount: AllowedOptionCount;
  correctOptionIndex: number;
  explanation: string;
  category: string;
  difficulty: string;
  imageUrl: string | null;
  explanationImageUrl: string | null;
  textFr: string;
  explanationFr: string;
  referenceUrl: string;
}) {
  const n = form.optionCount;
  return {
    text: form.text.trim(),
    options: form.options.slice(0, n).map((o) => o.trim()),
    optionsFr: form.optionsFr.slice(0, n).map((o) => o.trim()),
    correctOptionIndex: Math.min(Math.max(form.correctOptionIndex, 0), n - 1),
    explanation: form.explanation.trim(),
    category: form.category.trim(),
    difficulty: form.difficulty,
    imageUrl: form.imageUrl,
    explanationImageUrl: form.explanationImageUrl,
    textFr: form.textFr.trim(),
    explanationFr: form.explanationFr.trim(),
    referenceUrl: form.referenceUrl.trim(),
  };
}
